import { useEffect, useState } from "react";
import api from "../lib/api";

const cache = new Map(); // restaurantId -> permissions

/**
 * Loads `/restaurants/{rid}/me/permissions` and caches by restaurant. Returns
 * `{ role, flags, effective, loading }`. Owners always have all effective
 * flags = true. Used for UI gating (CTAs, buttons) — backend keeps enforcing
 * its own RBAC separately.
 */
export default function useUserPermissions(restaurantId) {
  const [perms, setPerms] = useState(() => cache.get(restaurantId) || null);
  const [loading, setLoading] = useState(!cache.has(restaurantId));

  useEffect(() => {
    if (!restaurantId) return;
    if (cache.has(restaurantId)) {
      setPerms(cache.get(restaurantId));
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await api.request(`/restaurants/${restaurantId}/me/permissions`);
        if (cancelled) return;
        cache.set(restaurantId, data);
        setPerms(data);
      } catch {
        // Fail-open: assume only role allowed
        setPerms({ role: "owner", flags: {}, effective: { can_open_cash: true, can_edit_accounting: true } });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [restaurantId]);

  return {
    role: perms?.role,
    flags: perms?.flags || {},
    effective: perms?.effective || {},
    can: (key) => Boolean(perms?.effective?.[key]),
    loading,
  };
}

export function clearPermissionsCache(restaurantId) {
  if (restaurantId) cache.delete(restaurantId);
  else cache.clear();
}
